/** @jsx jsx */
import React from 'react';
import { jsx, css } from '@emotion/core';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Hidden, Typography, useMediaQuery, useTheme } from '@material-ui/core';

import ClassicSmall from 'assets/banners/action-defi-banner-small.png';
import ClassicLarge from 'assets/banners/action-defi-banner-large.png';
import DealSmall from 'assets/banners/action-deal-banner-small.png';
import DealLarge from 'assets/banners/action-deal-banner-large.png';
import PhotoSmall from 'assets/banners/action-photo-banner-small.png';
import PhotoLarge from 'assets/banners/action-photo-banner-large.png';
import ListSmall from 'assets/banners/action-list-banner-small.png';
import ListLarge from 'assets/banners/action-list-banner-large.png';
import {
  ICustomTheme,
  ChallengeDatesType,
  ChallengeTypeType,
} from 'interfaces';
import { CountDown } from 'components';
import { getChallengePhase } from 'core/utils/challenges.helpers';
import { CHALLENGE, CHALLENGE_PHASE } from 'constants/challenges';

const banner = {
  [CHALLENGE.CLASSIC]: {
    small: ClassicSmall,
    large: ClassicLarge,
  },
  [CHALLENGE.DEAL]: {
    small: DealSmall,
    large: DealLarge,
  },
  [CHALLENGE.LIST]: {
    small: ListSmall,
    large: ListLarge,
  },
  [CHALLENGE.PHOTO]: {
    small: PhotoSmall,
    large: PhotoLarge,
  },
  default: {
    small: ClassicSmall,
    large: ClassicLarge,
  },
};

const StyledBanner = styled.div<{
  theme: ICustomTheme;
  type: ChallengeTypeType;
  half: boolean;
  src: string;
}>(({ theme, type, half, src }) => ({
  background: `url(${
    src ||
    banner[type || 'default']?.[half ? 'small' : 'large'] ||
    banner.default[half ? 'small' : 'large']
  })`,
  backgroundColor: theme.palette.grey[300],
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  height: theme.layout.widgets.actionBannerHeight,
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    height: theme.layout.widgets.actionBannerHeightMobile,
  },
}));

const StyledTitleContainer = styled.div<{
  theme: ICustomTheme;
  hasTitle?: boolean;
}>(({ theme, hasTitle }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(hasTitle ? -5 : -3),
  position: 'relative',
}));

const cssBlock = css({ display: 'block' });

const StyledTitle = styled(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ disabled, highlighted, ...props }) => <Typography {...props} />,
  // voluntarily exclude 'disabled' & 'highlighted' props to not give them to Mui component
)<{ theme: ICustomTheme; disabled: boolean; highlighted: boolean }>(
  ({ theme, disabled, highlighted }) => {
    let backgroundColor = theme.colors.paletteFromBO.secondary;
    if (highlighted) {
      backgroundColor = theme.colors.paletteFromBO.primary;
    }
    if (disabled) {
      // eslint-disable-next-line prefer-destructuring
      backgroundColor = theme.colors.paletteFromBO.tertiary;
    }

    return {
      backgroundColor,
      borderRadius: theme.shape.borderRadius,
      color: theme.colors.white,
      minHeight: 30 + theme.spacing(2),
      // 30 is the text height, spacing(2) is the padding,
      // it ensures the block to have the same height even when it's empty
      maxWidth: '80%',
      padding: theme.spacing(1, 4),
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        minHeight: 22 + theme.spacing(2),
        // 22 is the text height
      },
    };
  },
);

/**
 * Colors:
 *
 * DEAL
 * - Before sign-in = grey
 *
 * - During sign-in
 *  + accepted = burgundy
 *  + undecided = burgundy
 *  + refused = grey
 *
 * - Before action
 *  + accepted = burgundy
 *  + undecided = grey
 *  + refused = grey
 *
 * - During action
 *  + accepted = yellow
 *  + undecided = grey
 *  + refused = grey
 *
 * - After action = grey
 *
 * CLASSIC/PHOTO/LIST
 * - Before action = burgundy
 * - During action = yellow
 * - After action = grey
 */

const Banner = ({
  image,
  challengeType,
  dates,
  isParticipating,
  title,
  half,
}: {
  image: string;
  challengeType: ChallengeTypeType;
  dates: ChallengeDatesType;
  isParticipating: boolean | undefined;
  title?: string;
  half: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const currentPhase = getChallengePhase(dates);
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const highlighted = currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE;

  const disabled =
    currentPhase === CHALLENGE_PHASE.BEFORE_DIFFUSION ||
    currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE ||
    currentPhase === CHALLENGE_PHASE.AFTER_DIFFUSION ||
    isParticipating === false;

  return (
    <React.Fragment>
      <StyledBanner type={challengeType} half={half} src={image} />
      {isParticipating === false ? (
        <StyledTitleContainer>
          <StyledTitle variant={isDownSm ? 'body2' : 'body1'} disabled>
            <Hidden smDown>{t('challenges.challenge_refused')}</Hidden>
            <Hidden mdUp>
              <span>{t('challenges.challenge_refused')}</span>
            </Hidden>
          </StyledTitle>
        </StyledTitleContainer>
      ) : (
        <React.Fragment>
          {!currentPhase && (
            <StyledTitleContainer>
              <StyledTitle variant={isDownSm ? 'body2' : 'body1'} disabled />
            </StyledTitleContainer>
          )}

          {currentPhase === CHALLENGE_PHASE.BEFORE_SIGN_IN && (
            <StyledTitleContainer>
              <CountDown
                before={dates.signIn.start.raw}
                highlighted={highlighted}
                disabled={disabled}
              />
            </StyledTitleContainer>
          )}

          {currentPhase === CHALLENGE_PHASE.DURING_SIGN_IN && (
            <StyledTitleContainer>
              <StyledTitle
                variant={isDownSm ? 'body2' : 'body1'}
                highlighted={highlighted}
                disabled={disabled}
              >
                {t('widgets.sign_in_until', { until: dates.signIn.end.string })}
              </StyledTitle>
            </StyledTitleContainer>
          )}

          {(currentPhase === CHALLENGE_PHASE.BEFORE_CHALLENGE ||
            currentPhase === CHALLENGE_PHASE.BEFORE_DIFFUSION) && (
            <StyledTitleContainer>
              <CountDown
                before={dates.challenge.start.raw}
                highlighted={highlighted}
                disabled={disabled}
              />
            </StyledTitleContainer>
          )}

          {currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE && (
            <StyledTitleContainer hasTitle={!!title}>
              <StyledTitle
                variant={isDownSm ? 'body2' : 'body1'}
                highlighted={highlighted}
                disabled={disabled}
              >
                {title ? (
                  <span css={cssBlock}>{title}</span>
                ) : (
                  <React.Fragment>
                    <Hidden smDown>{t('challenges.challenge_running')}</Hidden>
                    <Hidden mdUp>
                      <span>{t('challenges.challenge_running')}</span>
                    </Hidden>{' '}
                  </React.Fragment>
                )}
                <strong css={{ textTransform: 'lowercase' }}>
                  {dates.challenge.end
                    ? t('time.from_to', {
                        from: dates.challenge.start.string,
                        to: dates.challenge.end.string,
                      })
                    : t('time.from', { from: dates.challenge.start.string })}
                </strong>
              </StyledTitle>
            </StyledTitleContainer>
          )}

          {currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE && (
            <StyledTitleContainer hasTitle={!!title}>
              <StyledTitle
                variant={isDownSm ? 'body2' : 'body1'}
                highlighted={highlighted}
                disabled={disabled}
              >
                {title ? (
                  <React.Fragment>
                    <span css={cssBlock}>{title}</span>
                    <strong css={{ textTransform: 'lowercase' }}>
                      {dates.challenge.end
                        ? t('time.from_to', {
                            from: dates.challenge.start.string,
                            to: dates.challenge.end.string,
                          })
                        : t('time.from', {
                            from: dates.challenge.start.string,
                          })}
                    </strong>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Hidden smDown>{t('challenges.challenge_over')}</Hidden>
                    <Hidden mdUp>
                      <span>{t('challenges.challenge_over')}</span>
                    </Hidden>{' '}
                    <strong css={{ textTransform: 'lowercase' }}>
                      {t('time.to', { to: dates.challenge.end.string })}
                    </strong>
                  </React.Fragment>
                )}
              </StyledTitle>
            </StyledTitleContainer>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

Banner.defaultProps = {
  image: '',
  isParticipating: undefined,
};

export default Banner;
